<script setup lang="ts">
import { useRoute } from "vue-router";
import { IconArrowLeftRegular, IconBagRegular } from "@packages/icon-library";
import { usePlaceMenuStore, usePlaceStore } from "@/modules/place-module";
import {
  computed,
  ComputedRef,
  onBeforeMount,
  onUnmounted,
  ref,
  watch,
} from "vue";
import PlaceOtherProducts from "./PlaceOtherProducts.vue";
import PlaceProductSlider from "./PlaceProductSlider.vue";
import { UiBadge, UiButton, UiCounter } from "@packages/component-library";
import ShowMoreText from "@/components/ShowMoreText.vue";
import PlaceProductSkeleton from "./PlaceProductSkeleton.vue";
import { useCartStore } from "@/modules/cart-module";
import { IUser, useUserStore } from "@/modules/user-module";
import { useI18n } from "vue-i18n";
import PlaceAuthBanner from "@/modules/place-module/components/PlaceMenu/PlaceAuthBanner.vue";

const { t } = useI18n();
const route = useRoute();
const placeStore = usePlaceStore();
const placeMenuStore = usePlaceMenuStore();
const cartStore = useCartStore();
const userStore = useUserStore();
const place = computed(() => placeStore.place);
const product = computed(() => placeMenuStore.product);
const count = ref(0);
const user: ComputedRef<null | IUser> = computed(() => userStore.user);

const loadProductInfo = () => {
  placeMenuStore.getPlaceProduct(route.query.product).then(() => {
    count.value = product.value?.product.count_in_cart;
  });
};

const routeQueries = computed(() => {
  const queries = structuredClone(route.query);
  delete queries.product;
  return queries;
});

watch(
  () => route.query.product,
  (newValue) => {
    if (newValue) {
      count.value = 0;
      loadProductInfo();
    }
  }
);

const addProductInCart = () => {
  if (!user.value) {
    return;
  }

  count.value++;
  cartStore.updateCartProduct({
    product_id: product.value?.product.id,
    count: count.value,
  });
};

const updateProductInCart = (change: "add" | "remove") => {
  if (change === "add") {
    count.value++;
  }
  if (change === "remove") {
    count.value--;
  }
  cartStore.updateCartProduct({
    product_id: product.value?.product.id,
    count: count.value,
  });
};

const updateProductCountInCart = (newCount: number) => {
  count.value = newCount;

  cartStore.updateCartProduct({
    product_id: product.value?.product.id,
    count: count.value,
  });
};

onBeforeMount(() => {
  loadProductInfo();
});

onUnmounted(() => {
  placeMenuStore.clearPlaceProduct();
});
</script>

<template>
  <div v-if="product" class="place-product">
    <div class="place-product__content">
      <div class="place-product__header">
        <RouterLink :to="{ query: routeQueries }">
          <IconArrowLeftRegular />
        </RouterLink>

        <h5>{{ place?.place.title }}</h5>

        <RouterLink v-if="user" :to="{ name: 'CartView' }">
          <span v-if="cartStore.getCartProductsLength">
            {{
              cartStore.getCartProductsLength <= 9
                ? cartStore.getCartProductsLength
                : "9+"
            }}
          </span>
          <IconBagRegular />
        </RouterLink>
      </div>

      <PlaceProductSlider />

      <div class="place-product__info">
        <p>
          {{ product.product.price }} {{ product.product.currency }}
          <template v-if="product.product.discount_percent">
            <del>{{ product.product.old_price }}</del>
            <UiBadge
              :label="`-${product.product.discount_percent}%`"
              color="red"
              type="soft"
              size="m"
            />
          </template>
        </p>

        <h3>{{ product.product.title }}</h3>

        <ShowMoreText :text="product.product.description" />
      </div>

      <PlaceAuthBanner v-if="!user" />

      <PlaceOtherProducts v-if="product.other_products.length" />
    </div>

    <div v-if="product.order_allowed" class="place-product__actions">
      <UiButton
        v-if="count === 0"
        :label="t('place.product.addToCart')"
        size="m"
        :disabled="product.product.price === 0 || !user"
        data-test-id="placeProduct-button-addInCart"
        @click="addProductInCart"
      />

      <div v-else class="place-product__counter">
        <UiButton
          :label="t('place.product.goToCart')"
          size="m"
          :to="{ name: 'CartView' }"
          data-test-id="placeProduct-button-cart"
        />

        <UiCounter
          v-model="count"
          size="m"
          :max="999"
          @increment="updateProductInCart('add')"
          @decrement="updateProductInCart('remove')"
          @update="updateProductCountInCart"
        />
      </div>
    </div>
  </div>

  <PlaceProductSkeleton v-else />
</template>

<style scoped lang="scss">
.place-product {
  height: 100%;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr auto;
  align-items: start;

  &__content {
    overflow: auto;
    overscroll-behavior: contain;
    height: 100%;
  }

  &__header {
    padding: calc($base-space * 2) calc($base-space * 8);
    display: grid;
    grid-template-columns: 32px 1fr 48px;
    align-items: center;
    gap: calc($base-space * 4);

    @media (max-width: 1023px) {
      padding: calc($base-space * 2) calc($base-space * 6) 0;
      gap: calc($base-space * 3);
      grid-template-columns: 32px 1fr 40px;
    }

    @media (max-width: 767px) {
      padding: calc($base-space * 2) calc($base-space * 4) 0;
    }

    > a {
      &:first-of-type {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;

        > i {
          background-color: var(--color-semantic-content-normal-primary);
        }
      }

      &:not(:first-of-type) {
        width: 48px;
        height: 48px;
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;

        @media (max-width: 1023px) {
          width: 40px;
          height: 40px;
        }

        > .icon {
          background-color: var(--color-semantic-content-normal-primary);

          @media (max-width: 1023px) {
            width: 20px;
            height: 20px;
          }
        }

        > span:not(.icon) {
          width: 16px;
          height: 16px;
          @include caption();
          border-radius: 50%;
          background-color: var(--color-semantic-background-normal-brand);
          color: var(--color-semantic-content-normal-static-dark);
          display: flex;
          align-items: center;
          justify-content: center;
          position: absolute;
          z-index: 2;
          top: 0;
          right: 0;
        }
      }
    }

    > h5 {
      @include h5();
      @include max-stroke(1);
      color: var(--color-semantic-content-normal-secondary);
    }
  }

  &__info {
    padding: calc($base-space * 6) ($base-space * 8);

    @media (max-width: 1023px) {
      padding: calc($base-space * 6) calc($base-space * 6);
    }

    @media (max-width: 767px) {
      padding: calc($base-space * 5) calc($base-space * 4);
    }

    > P {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      gap: calc($base-space * 3);
      @include h4();
      color: var(--color-semantic-content-normal-primary);

      @media (max-width: 1023px) {
        gap: calc($base-space * 2);
      }

      > del {
        color: var(--color-semantic-content-normal-tertiary);
        @include body-m();
      }
    }

    > h3 {
      margin: calc($base-space * 6) 0 0;
      @include body-bold-l();
      color: var(--color-semantic-content-normal-primary);

      @media (max-width: 767px) {
        margin: calc($base-space * 5) 0 0;
      }
    }

    > .show-more-text {
      margin: calc($base-space * 2) 0 0;

      @media (max-width: 1023px) {
        margin: calc($base-space * 3) 0 0;
      }
    }
  }

  &__actions {
    padding: calc($base-space * 4) ($base-space * 8) calc($base-space * 8);

    @media (max-width: 1023px) {
      padding: calc($base-space * 3) ($base-space * 6) calc($base-space * 6);
    }

    @media (max-width: 767px) {
      padding: calc($base-space * 3) ($base-space * 4) calc($base-space * 4);
    }

    > button {
      width: 100%;
    }
  }

  &__counter {
    display: grid;
    grid-template-columns: 1fr 170px;
    gap: calc($base-space * 4);

    @media (max-width: 1023px) {
      gap: calc($base-space * 3);
    }

    @media (max-width: 424px) {
      display: flex;
      flex-flow: column-reverse;
    }

    > a {
      width: 100%;
    }
  }
}
</style>
